import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Plus, Camera, Briefcase, ChevronLeft, ChevronRight, ChevronDown, Check, LogIn, LogOut, ClipboardList, RefreshCw, CalendarX2, ReceiptText, AlertTriangle, FileText, CalendarClock, Package, CalendarRange } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TaskApprovalBand } from "@/components/home/TaskApprovalBand";
import { useAuth } from "@/context/AuthContext";
import { storage } from "@/lib/storage";
import { formatCZK, formatCZKCompact, plur } from "@/lib/format";
import { effectiveAttendanceMode, correctedMode } from "@/lib/attendanceMode";
import { useVisiblePolling } from "@/hooks/useVisiblePolling";
import { warmGps } from "@/lib/gps";
import { vocative } from "@/lib/vocative";
import { parseLocal, taskItem, spansDay, day0, addDays as addDaysD, hm as hmOf, DOW_LONG, MONTHS_GEN } from "@/lib/taskCalendar";
import HoursChart from "@/components/HoursChart";
import { AbsenceDialog } from "@/components/absences/AbsenceDialog";
import { STATUS_LABEL, daysWord } from "@/lib/absences";
import MyShiftBlock from "@/components/MyShiftBlock";
import ApprovalQueue from "@/components/ApprovalQueue";
import SubInvoiceApproval from "@/components/SubInvoiceApproval";
import MaterialApproval from "@/components/MaterialApproval";
import MyAdvancesCard from "@/components/cashbox/MyAdvancesCard";
import SitePicker from "@/components/mobile/SitePicker";
import { NewTaskDialog } from "@/components/tasks/NewTaskDialog";
import { shiftStateFrom, elapsedHm, hoursLabel } from "@/components/mobile/shiftState";
import { pragueMin } from "@/components/mobile/todayFeed";
import CraneArt from "@/components/home/CraneArt";
import HomeSearch from "@/components/home/HomeSearch";
import ShiftEndFixDialog, { needsEndFix } from "@/components/shared/ShiftEndFixDialog";
import { TodayCard, TomorrowCard, DeadlinesCard } from "@/components/home/HomeCards";
import { useSlidingPill } from "@/components/shared/Pill";
import { useListNav, Kbd } from "@/components/shared/ListNav";
import EconomicsDocuments from "@/pages/EconomicsDocuments";
import { useHomePrefs } from "@/components/home/HomePrefs";
import { GuideCard } from "@/components/GuideNudge";

// Úvod majitele, stavbyvedoucího a asistentky na počítači a tabletu (236, „Rozvržení 4“);
// od 246 i dělníka, skladníka a subdodavatele (Příchod · Moje hodiny · Žádosti, pod tím Dnes a Zítra).
// Nahoře pruh přes celou šířku se třemi snímky (Firma dnes · Moje směna a docházka ·
// Co hoří — pořadí 246, 250 místo Faktury a sklad), pod ním karta Dnes, dole Plán na zítřek a Termíny. Telefon má dál svůj
// přehled (ManagerHome). Příchod a odchod obsluhuje MyShiftBlock (headless) přes /?shift=1
// — stejné GPS, fotky i výkaz jako všude jinde.

const TZ = "Europe/Prague";
const HOLD_MS = 900;
const SHIFT = 1, FIN = 2;   // pořadí snímků v pruhu (246: směna před fakturami)

// šířka prvku (pruh a mřížky se řídí místem, které mají — sidebar může být sbalený)
function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// „9,5 mil. Kč“ → velké číslo + menší jednotka
function splitMoney(v) {
  const s = formatCZKCompact(v);
  const m = /^(-?[\d\s\u00a0,.]*\d)\s*(\D.*)$/.exec(s);
  return m ? [m[1].trim(), m[2]] : [s, ""];
}
const hoursParts = (h) => { const [i, d] = (Math.round((Number(h) || 0) * 10) / 10).toFixed(1).split("."); return [i, d === "0" ? " h" : `,${d} h`]; };
const ini2 = (name) => String(name || "?").replace(/s\.r\.o\.|a\.s\./gi, "").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const hoursOrMin = (h) => (Number(h) > 0 && Number(h) < 1 ? `${Math.max(1, Math.round(Number(h) * 60))} min` : hoursLabel(h));
const czDay = (s) => { const [, m, d] = String(s || "").slice(0, 10).split("-").map(Number); return d ? `${d}. ${m}.` : ""; };

// ---------------- tlačítko příchodu v hlavičce ----------------
// Klik = karta Moje směna (a seznam staveb, když ještě nejsem v práci), podržení = příchod
// na předvybranou stavbu / odchod / výkaz. Barvy jako mobilní lišta: terakota, zelená, tmavá.
function HeaderShiftButton({ state, hm, label, sub, onTap, onHold }) {
  const [holding, setHolding] = useState(false);
  const timer = useRef(null);
  const origin = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const cancel = () => { clearTimeout(timer.current); timer.current = null; origin.current = null; setHolding(false); };
  const down = (e) => {
    if (e.button != null && e.button !== 0) return;
    origin.current = { x: e.clientX, y: e.clientY };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* starší prohlížeč */ }
    setHolding(true);
    if (state !== "draft") warmGps();
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; origin.current = null; setHolding(false); onHold?.(); }, HOLD_MS);
  };
  const move = (e) => { const o = origin.current; if (timer.current && o && (Math.abs(e.clientX - o.x) > 40 || Math.abs(e.clientY - o.y) > 40)) cancel(); };
  const up = () => { if (!timer.current) return; cancel(); onTap?.(); };
  const bg = state === "working" ? "hsl(var(--success))" : state === "ended" ? "hsl(var(--secondary))" : "hsl(var(--accent-orange))";
  const ring = state === "working" ? "#17181b" : state === "ended" ? "hsl(var(--accent-orange))" : "hsl(var(--success))";
  return (
    <div className="flex items-center gap-3 shrink-0">
      <button type="button" data-testid="home-shift-btn" aria-label={`${label}${sub ? ` — ${sub}` : ""}. Klik otevře Moji směnu, podržení potvrdí.`}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel} onContextMenu={(e) => e.preventDefault()}
        onClick={(e) => { if (e.detail === 0) onHold?.(); }}
        className={`pp-hold relative w-[52px] h-[52px] rounded-full grid place-items-center ${state === "ended" ? "text-foreground" : "text-white"} ${holding ? "is-holding" : ""}`}
        style={{ background: bg }}>
        <svg aria-hidden="true" viewBox="0 0 74 74" width="62" height="62" className="absolute -left-[5px] -top-[5px] -rotate-90 pointer-events-none">
          <circle className={`pp-ring ${holding ? "on" : ""}`} cx="37" cy="37" r="34.5" stroke={ring} />
        </svg>
        {state === "working" ? <span className="font-heading text-[13px] font-extrabold tabular-nums">{hm}</span>
          : state === "ended" ? <Check size={22} strokeWidth={2.6} aria-hidden="true" />
          : state === "draft" ? <ClipboardList size={21} aria-hidden="true" />
          : <LogIn size={22} strokeWidth={2.3} aria-hidden="true" />}
      </button>
      <span className="hidden sm:block leading-tight min-w-0 max-w-[200px]" aria-hidden="true">
        <span className="block text-[14.5px] font-bold truncate">{label}</span>
        <span className="block text-[13px] text-muted-foreground truncate">{sub}</span>
      </span>
    </div>
  );
}

// ---------------- snímek 1: Firma dnes ----------------
function Kpi({ label, big, unit, btn, onBtn, testid, narrow }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-sm font-semibold text-white/70 truncate">{label}</div>
      <div className={`font-heading font-extrabold tracking-tight leading-none tabular-nums whitespace-nowrap mt-2 mb-3.5 ${narrow ? "text-[38px]" : "text-[44px] xl:text-[56px]"}`}>
        {big}<span className="text-xl xl:text-[22px] tracking-normal">{unit}</span>
      </div>
      {btn && <button type="button" onClick={onBtn} className={`h-9 rounded-full border border-white/15 bg-white/[.04] font-bold whitespace-nowrap hover:bg-white/10 ${narrow ? "px-3 text-[13px]" : "px-4 text-sm"}`}>{btn}</button>}
    </div>
  );
}

function FirmSlide({ work, week, role, wide, narrow, terra, nav, onNewTask }) {
  const k = work?.kpi || {};
  const [wi, wd] = hoursParts(week);
  const nLive = k.projectsInProgressMine ?? k.projectsInProgress ?? 0;
  const money = !!k.projectsInProgressValue;   // bez cen ve smlouvách aspoň počet zakázek
  const [mBig, mUnit] = money ? splitMoney(k.projectsInProgressValue) : [String(nLive), nLive === 1 ? "zakázka" : nLive >= 2 && nLive <= 4 ? "zakázky" : "zakázek"];
  const acts = [
    [BookOpen, "Zápis do deníku", () => nav("/denik")],
    [Plus, "Nový úkol", onNewTask],
    [Camera, "Fotka ze stavby", () => nav("/galerie?nahrat=1")],
    [Briefcase, "Nová zakázka", () => nav("/zakazky?nova=1")],
  ];
  return (
    <div className="relative">
      <div className={`grid ${narrow ? "gap-4" : "gap-6"} ${wide ? "grid-cols-3" : "grid-cols-1 sm:grid-cols-3"}`} style={wide ? { maxWidth: "calc(100% - 300px)" } : undefined}>
        <Kpi testid="kpi-week" narrow={narrow} label={narrow ? "Můj týden" : "Tento týden odpracováno"} big={wi} unit={wd} btn={narrow ? "Docházka" : "Moje docházka"} onBtn={() => nav("/dochazka")} />
        <Kpi testid="kpi-atwork" narrow={narrow} label={narrow ? "Teď v práci" : "Firma · teď v práci"} big={work ? k.clockedInCount ?? 0 : "–"} unit={work ? ` / ${plur(k.employeeCount || 0, "člověk", "lidé", "lidí")}` : ""}
          btn="Tabule" onBtn={() => nav("/tabule")} />
        <Kpi testid="kpi-live" narrow={narrow} label={money && !narrow ? `V realizaci · ${plur(nLive, "zakázka", "zakázky", "zakázek")}` : "V realizaci"} big={work ? mBig : "–"} unit={work ? ` ${mUnit}` : ""}
          btn="Zakázky" onBtn={() => nav("/zakazky")} />
      </div>
      <div className="flex flex-wrap gap-2 mt-5 relative z-[1]" style={wide ? { maxWidth: "calc(100% - 300px)" } : undefined}>
        {acts.map(([Icon, label, fn]) => (
          <button key={label} type="button" onClick={fn} className="pp-band-qa h-10 px-4 rounded-full border border-white/15 bg-white/[.03] text-sm font-bold inline-flex items-center gap-2 hover:bg-white/10">
            <Icon size={16} aria-hidden="true" />{label}
          </button>
        ))}
      </div>
      {wide && <CraneArt color={terra ? "#ffffff" : undefined} className={`absolute -right-1 -bottom-6 w-[300px] h-[188px] pointer-events-none ${terra ? "opacity-60" : "opacity-90"}`} />}
    </div>
  );
}

// ---------------- snímek 3: Co hoří (250) ----------------
// Jeden seznam toho, co čeká na vedoucího: faktury (ke schválení / po splatnosti), žádosti o volno,
// sklad pod minimem a harmonogram po termínu. Nahoře filtr podle druhu (volba se pamatuje).
const HOT_KINDS = [
  { key: "faktury", label: "Faktury", icon: FileText },
  { key: "zadosti", label: "Žádosti", icon: CalendarClock },
  { key: "sklad", label: "Sklad", icon: Package },
  { key: "harmonogram", label: "Harmonogram", icon: CalendarRange },
];

export function hotItems(work, abs) {
  const inv = work?.invoices || { sub: [], payable: [], receivable: [] };
  const late = (n) => `${plur(n, "den", "dny", "dní")} po splatnosti`;
  const out = [];
  inv.sub.forEach((r) => out.push({ key: `s-${r.id}`, kind: "faktury", title: r.supplier, money: r.amount, act: "Schválit", to: { sub: r.id },
    sub: ["ke schválení", r.projectName || "zakázka neurčena", r.invoiceNumber].filter(Boolean).join(" · "), note: r.dueDate ? `splatná ${czDay(r.dueDate)}` : "od subdodavatele" }));
  inv.payable.forEach((r) => out.push({ key: `p-${r.id}`, kind: "faktury", title: r.supplier, money: r.amount, act: "Zaplatit", crit: r.daysOverdue > 14, late: true,
    sub: ["máme zaplatit", r.projectName, r.invoiceNumber].filter(Boolean).join(" · "), note: late(r.daysOverdue), to: `/ekonomika/doklady?open=faktura:${r.id}`,
    actTo: `/ekonomika/platby?pay=${r.id}`, doc: { kind: "faktura", id: r.id } }));   // 341: Zaplatit → Platby s otevřeným zápisem úhrady; 343: detail přímo na Přehledu
  (inv.receivable || []).forEach((r) => out.push({ key: `r-${r.id}`, kind: "faktury", title: r.client, money: r.amount, act: "Upomenout", crit: r.daysOverdue > 14, late: true,
    sub: ["nezaplatili nám", r.projectName, r.invoiceNumber].filter(Boolean).join(" · "), note: late(r.daysOverdue), to: `/ekonomika/doklady?open=vydana:${r.id}` }));
  (abs || []).forEach((a) => out.push({ key: `a-${a.id}`, kind: "zadosti", title: a.user_name, act: a.ack ? "Beru na vědomí" : "Schválit", abs: a,
    sub: [a.type_label, a.makeup && "napracuje", a.note && `„${a.note}“`].filter(Boolean).join(" · "), value: a.when, to: "/dochazka?tab=sichtovnice" }));
  (work?.stockLow || []).forEach((x) => out.push({ key: `k-${x.id}`, kind: "sklad", title: x.name, act: "Objednat", crit: !x.qty, late: !x.qty,
    sub: `${x.warehouseName} · minimum ${x.min} ${x.unit}`, value: x.qty ? `${x.qty} / ${x.min} ${x.unit}` : "došlo", to: "/sklad?pod=minimum" }));
  (work?.hot || []).filter((h) => h.kind === "schedule").forEach((h) => out.push({ key: `h-${h.id}`, kind: "harmonogram", title: h.title, act: "Přeplánovat",
    crit: true, late: true, sub: h.subtitle, value: "po termínu", to: h.href }));
  const order = HOT_KINDS.map((k) => k.key);
  const rank = (x) => (x.crit ? 0 : x.late ? 1 : 2);   // nejdřív kritické, pak po termínu, pak ke schválení
  return out.map((x, i) => ({ ...x, i })).sort((a, b) => rank(a) - rank(b) || order.indexOf(a.kind) - order.indexOf(b.kind) || a.i - b.i);
}

// 340: `to` je buď cesta (text), nebo { sub: id } faktury subdodavatele. U textu je `x.to.sub`
// funkce String.prototype.sub — šla do setSubId a React ji zavolal → celá aplikace spadla.
export const hotOpen = (x, nav, onSub) => (x.to && typeof x.to === "object" && x.to.sub ? onSub(x.to.sub) : nav(x.to));
// Tlačítko vpravo: nepřítomnost schválí na místě, faktura k zaplacení vede do Plateb (341), jinak jako klik na řádek.
export const hotAct = (x, nav, onSub, onAbs) => (x.abs ? onAbs(x) : x.actTo ? nav(x.actTo) : hotOpen(x, nav, onSub));

function HotSlide({ items, wide, nav, onSub, onAbs, busy, active = true, onDocChanged }) {
  const [filter, setFilter] = useState(() => storage.get("hot_filter") || "all");
  const counts = Object.fromEntries(HOT_KINDS.map((k) => [k.key, (items || []).filter((x) => x.kind === k.key).length]));
  const shown = filter !== "all" && counts[filter] ? filter : "all";
  const hotPill = useSlidingPill(shown, (items || []).length);   // 385: bílý podklad filtru dojede s pružinou
  const list = (items || []).filter((x) => shown === "all" || x.kind === shown);
  const pick = (k) => { setFilter(k); try { storage.set("hot_filter", k); } catch { /* noop */ } };
  const open = (x) => hotOpen(x, nav, onSub);
  // 343: faktura k zaplacení se otevře ve stejném detailu jako v Dokladech, ale přímo tady (bez přesměrování).
  // ↑↓ / J K nebo najetí myší vybere řádek, mezerník otevře (355: Enter ne); v detailu ↑↓ listují fakturami z „Co hoří".
  const docRef = useRef(null);
  const docs = list.filter((x) => x.doc);
  // detail zůstane připojený, i když poslední faktura po zaplacení z Co hoří zmizí — jinak by se zavřel pod rukama
  const [docsOn, setDocsOn] = useState(false);
  useEffect(() => { if (docs.length) setDocsOn(true); }, [docs.length]);
  const openItem = (x) => {
    if (!x.doc || !docRef.current) return open(x);
    docRef.current(x.doc.kind, x.doc.id, { items: docs.map((d) => d.doc), onIndex: (i) => kb.setSel(list.indexOf(docs[i])) });
  };
  const kb = useListNav("hot", list.length, (i) => openItem(list[i]), { enabled: active });
  const chip = (key, label, n, Icon) => (
    <button key={key} type="button" onClick={() => pick(key)} disabled={key !== "all" && !n} data-testid={`hot-filter-${key}`} aria-pressed={shown === key} data-pill-on={shown === key || undefined}
      className={`relative z-[1] h-9 px-3.5 rounded-full text-[13.5px] font-bold whitespace-nowrap inline-flex items-center gap-1.5 border transition-colors disabled:opacity-40 ${shown === key
        ? (hotPill.ready ? "text-[#0f0f10] border-transparent" : "bg-white text-[#0f0f10] border-white") : "bg-white/[.04] border-white/10 text-white/80 hover:text-white"}`}>
      {Icon && wide && <Icon size={15} aria-hidden="true" />}{label}<span className="tabular-nums opacity-70">{n}</span>
    </button>
  );
  return (
    <div className="pp-band-panel rounded-[18px] px-4 pt-3.5 pb-2.5 min-w-0" data-testid="band-hot">
      {(items || []).length > 0 && (
        <div className="flex items-center gap-2">
          <div ref={hotPill.ref} className="relative flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1 min-w-0" data-testid="hot-filter">
            {hotPill.pill("bg-white")}
            {chip("all", "Vše", items.length)}
            {HOT_KINDS.map((k) => chip(k.key, k.label, counts[k.key], k.icon))}
          </div>
          {wide && list.length > 0 && (   // 343: nápověda kláves jen na počítači
            <span className="ml-auto shrink-0 hidden lg:flex items-center gap-1.5 text-[12px] text-white/75 whitespace-nowrap" data-testid="hot-keys">
              <Kbd className="!bg-white/15 !border-white/25 !text-white">↑</Kbd><Kbd className="!bg-white/15 !border-white/25 !text-white">↓</Kbd> vybrat
              <Kbd className="!bg-white/15 !border-white/25 !text-white">␣</Kbd> otevřít
            </span>
          )}
        </div>
      )}
      {!items ? <div className="h-28 mt-3 rounded-2xl bg-white/10 animate-pulse motion-reduce:animate-none" />
        : !list.length ? <p className="flex items-center gap-2.5 py-2 text-[15px] font-bold" data-testid="hot-empty"><span className="w-8 h-8 rounded-full bg-success grid place-items-center"><Check size={16} strokeWidth={2.6} aria-hidden="true" /></span>Nic nehoří</p>
        : (
          <div className="pp-band-scroll mt-1.5 max-h-[312px] overflow-y-auto -mx-2">
            {list.map((x, i) => {
              const Icon = HOT_KINDS.find((k) => k.key === x.kind)?.icon || AlertTriangle;
              return (
                <div key={x.key} role="button" tabIndex={0} {...kb.rowProps(i)} onClick={() => { kb.setSel(i); openItem(x); }}
                  data-testid={`hot-row-${x.key}`}
                  className={`grid ${wide ? "grid-cols-[34px_minmax(0,1fr)_auto_auto]" : "grid-cols-[34px_minmax(0,1fr)_auto]"} gap-3 items-center px-2 py-2.5 border-b border-white/[.12] last:border-b-0 cursor-pointer rounded-[8px] outline-none ${kb.sel === i ? "bg-white/[.12]" : ""}`}>
                  <span className={`w-[34px] h-[34px] rounded-[10px] grid place-items-center ${x.crit ? "bg-[#e5484d]/20 text-[#ff8a8d]" : "bg-white/10 text-white/80"}`}><Icon size={16} aria-hidden="true" /></span>
                  <span className="min-w-0">
                    <span className="block text-[14.5px] font-bold truncate">{x.title}</span>
                    <span className="block text-[12.5px] text-white/70 truncate">{x.sub}</span>
                  </span>
                  <span className="text-right whitespace-nowrap">
                    <span className="block font-heading text-base font-extrabold tabular-nums">{x.money != null ? formatCZK(x.money) : x.value}</span>
                    {x.note && <span className={`block text-xs font-semibold ${x.late ? "pp-band-late" : "text-white/70"}`}>{x.note}</span>}
                  </span>
                  {wide && (
                    <button type="button" disabled={busy === x.key} data-testid={`hot-act-${x.key}`}
                      onClick={(e) => { e.stopPropagation(); hotAct(x, nav, onSub, onAbs); }}
                      className={`h-9 px-3 min-w-[104px] rounded-full text-[13px] font-bold whitespace-nowrap justify-self-end ${x.abs ? "bg-success text-white hover:bg-success/90" : "bg-white/10 hover:bg-white/15"}`}>{x.act}</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      {docsOn && <EconomicsDocuments dialogsOnly openRef={docRef} onChanged={onDocChanged} />}
    </div>
  );
}

// ---------------- snímek 3: Směna a ke schválení (docházka | úkoly, 263) ----------------
function ShiftSlide({ st, sh, prefProj, week, wide, seeAtt, onPick, nav, role, onAttCount, reloadKey, onApproved, extra, terra, fix, onFix, seeTasks, attCount, taskCount, onTaskCount, active = false }) {
  const [queue, setQueue] = useState("att");   // 263: ke schválení — Docházka | Úkoly
  const qPill = useSlidingPill(queue, `${attCount}|${taskCount}`);   // 385
  const [, setTick] = useState(0);
  useEffect(() => { if (st.state !== "working") return undefined; const id = setInterval(() => setTick((n) => n + 1), 30000); return () => clearInterval(id); }, [st.state]);
  const daysheet = st.mode === "daysheet";
  const ov = sh?.ov;
  const since = st.since ? new Intl.DateTimeFormat("cs-CZ", { hour: "numeric", minute: "2-digit", timeZone: TZ }).format(new Date(st.since)) : "";
  const pill = (txt, live) => (
    <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white/[.18] text-[13.5px] font-extrabold">
      {live && <span className="w-2 h-2 rounded-full bg-white animate-pulse motion-reduce:animate-none" />}{txt}
    </span>
  );
  const big = "font-heading font-extrabold tracking-tight leading-[1.05] text-[34px] xl:text-[40px]";
  let body;
  if (!sh) body = <div className="h-32 rounded-2xl bg-white/10 animate-pulse motion-reduce:animate-none" />;
  else if (st.state === "working") {
    body = (<>
      {pill("V práci", true)}
      <div className="font-heading font-extrabold tabular-nums leading-none mt-3.5 mb-1 text-[48px] xl:text-[56px]" data-testid="home-shift-clock">{elapsedHm(st.since)} h</div>
      <div className="text-[15px] text-white/90 truncate"><b className="text-white">{ov?.shift?.project_name || (daysheet ? "Denní výkaz" : "V práci")}</b> · od {since}</div>
      <div className="text-[13.5px] text-white/85 mt-0.5">Odchod: podrž tlačítko vpravo nahoře</div>
    </>);
  } else if (st.state === "draft") {
    body = (<>
      {pill("Výkaz k vyplnění")}
      <h3 className={`${big} mt-3.5 mb-1.5`}>Vyplň výkaz</h3>
      <div className="text-[14px] text-white/90">Rozděl dnešní hodiny na zakázky.</div>
      <button type="button" onClick={() => nav("/?shift=1")} className="mt-4 h-11 px-5 rounded-full bg-white text-[#a84d2b] text-[15px] font-bold w-full md:w-auto">Vyplnit výkaz</button>
    </>);
  } else if (st.state === "ended") {
    body = (<>
      <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white/10 text-[13.5px] font-extrabold"><Check size={14} strokeWidth={2.6} aria-hidden="true" />{daysheet ? "Výkaz odeslán" : "Odhlášen"}</span>
      <div className="font-heading font-extrabold tabular-nums leading-none mt-3.5 mb-1 text-[48px] xl:text-[56px]">{hoursOrMin(st.hours)}</div>
      <div className="text-[14px] text-white/80">Dnes zapsáno do docházky{role === "OWNER" ? "" : " · čeká na schválení"}</div>
    </>);
  } else if (fix) {
    // 258: nedoplněný konec neodhlášené směny — bez něj se nepřihlásí, proto místo výběru stavby
    const d = new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", timeZone: TZ }).format(new Date(fix.check_in));
    body = (<>
      {pill("Nedokončená směna")}
      <h3 className={`${big} mt-3.5 mb-1.5`}>Doplň konec směny</h3>
      <div className="text-[14px] text-white/90" data-testid="home-fix-text">Nebyl jsi odhlášen {d} · {fix.project}.{fix.blocks_check_in !== false ? " Bez toho se dnes do práce nepřihlásíš." : ""}</div>
      <button type="button" onClick={() => onFix(fix)} data-testid="home-fix-end" className="mt-4 h-11 px-5 rounded-full bg-white text-[#a8281c] text-[15px] font-bold w-full md:w-auto">Doplnit konec</button>
    </>);
  } else if (daysheet) {
    body = (<>
      {pill("Mimo práci")}
      <h3 className={`${big} mt-3.5 mb-1.5`}>Denní výkaz</h3>
      <div className="text-[14px] text-white/90">Podrž tlačítko vpravo nahoře a začni den. Zakázky a hodiny vyplníš na konci dne.</div>
    </>);
  } else {
    body = (<>
      {pill("Mimo práci")}
      <button type="button" onClick={onPick} aria-haspopup="dialog" data-testid="home-pick-site"
        className={`${big} mt-3.5 mb-1.5 flex items-center gap-2.5 max-w-full text-left hover:opacity-90`}>
        <span className="truncate">{prefProj?.name || "Vyber stavbu"}</span><ChevronDown size={28} className="shrink-0 opacity-80 mt-1.5" aria-hidden="true" />
      </button>
      <div className="text-[14px] text-white/90">{prefProj ? "Příchod se zapíše na tuhle stavbu. Změníš ji klikem na název." : "Příchod se zapíše na stavbu, kterou vybereš."}</div>
    </>);
  }
  const pct = Math.min(100, (week / 40) * 100);
  const left = (
    <div className="min-w-0">
      {body}
      {extra}
      <div className="mt-6">
        <div className="flex justify-between text-sm text-white/85"><span>Tento týden</span><b className="text-white tabular-nums">{String(Math.round(week * 10) / 10).replace(".", ",")} / 40 h</b></div>
        <div className="h-1.5 rounded-full bg-black/20 mt-2 overflow-hidden"><div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} /></div>
      </div>
    </div>
  );
  if (!seeAtt) {
    // dělník (246): směna sama, vpravo jeřáb jako na snímku Firma dnes
    return (
      <div className="relative">
        <div style={wide ? { maxWidth: "calc(100% - 320px)" } : undefined}>{left}</div>
        {wide && <CraneArt color={terra ? "#ffffff" : undefined} className={`absolute -right-1 -bottom-6 w-[300px] h-[188px] pointer-events-none ${terra ? "opacity-60" : "opacity-90"}`} />}
      </div>
    );
  }
  return (
    <div className={`grid gap-6 items-start ${wide ? "grid-cols-[minmax(0,.8fr)_minmax(0,1.2fr)]" : "grid-cols-1"}`}>
      {left}
      <div className="min-w-0">
        {seeTasks && (
          <div ref={qPill.ref} role="tablist" aria-label="Ke schválení" className="relative flex gap-1.5 mb-2.5" data-testid="approve-switch">
            {qPill.pill("bg-white")}
            {[["att", "Docházka", attCount], ["tasks", "Úkoly", taskCount]].map(([k, l, n]) => (
              <button key={k} type="button" role="tab" aria-selected={queue === k} onClick={() => setQueue(k)} data-testid={`approve-switch-${k}`} data-pill-on={queue === k || undefined}
                className={`relative z-[1] h-9 px-3.5 rounded-full text-[13.5px] font-bold inline-flex items-center gap-2 transition-colors ${queue === k ? `${qPill.ready ? "" : "bg-white"} text-[#a84d2b]` : "bg-black/15 text-white hover:bg-black/25"}`}>
                {l}{n > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11.5px] font-extrabold grid place-items-center tabular-nums ${queue === k ? "bg-[#a84d2b] text-white" : "bg-white/25"}`}>{n}</span>}
              </button>
            ))}
          </div>
        )}
        {/* obě fronty zůstávají načtené (počty v záložce), vidět je jen vybraná */}
        <div hidden={queue !== "att"}>
          <ApprovalQueue variant="band" canSeeMoney={role === "OWNER"} reloadKey={reloadKey} onCount={onAttCount} onChanged={onApproved}
            onOpenAll={() => nav("/dochazka?ke-schvaleni=1")} keys={active && queue === "att"} />
        </div>
        {seeTasks && <div hidden={queue !== "tasks"}><TaskApprovalBand nav={nav} onCount={onTaskCount} reloadKey={reloadKey} keys={active && queue === "tasks"} /></div>}
      </div>
    </div>
  );
}

// ---------------- dělník, skladník, subdodavatel (246) ----------------
// Pruh: Příchod · Moje hodiny · Žádosti, pod ním rychlé akce; pod pruhem Dnes a Zítra.
const ABS_TONE = { pending: "bg-white/20", approved: "bg-[#2f9e6e]", rejected: "bg-black/30", cancelled: "bg-black/20" };
const isoToday = () => new Intl.DateTimeFormat("sv-SE", { timeZone: TZ }).format(new Date());

function WorkerAlerts({ alerts, nav, onFix }) {
  if (!alerts.length) return null;
  return (
    <div className="flex flex-col gap-2 mt-4" data-testid="worker-alerts">
      {alerts.map((a) => (
        <div key={a.key} className={`rounded-[14px] px-3.5 py-2.5 flex items-center gap-2.5 text-[13.5px] ${a.block ? "bg-[#7a1f12]/70" : "bg-black/20"}`}>
          <AlertTriangle size={16} className="shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1"><b>{a.title}</b>{a.sub ? <span className="text-white/85"> — {a.sub}</span> : null}</span>
          {a.btn && <button type="button" onClick={() => (a.fix ? onFix(a.fix) : nav(a.to))} data-testid={a.fix ? `alert-fix-${a.key}` : undefined} className="shrink-0 h-8 px-3 rounded-full bg-white text-[#a84d2b] text-[13px] font-bold">{a.btn}</button>}
        </div>
      ))}
    </div>
  );
}

// nadpis panelu v pruhu (žádosti dělníka) — 254: vráceno, 251 ho omylem smazal s Faktury a sklad
function PanelHead({ title, count, link, onLink, meta }) {
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-heading text-lg font-bold flex items-center gap-2 min-w-0"><span className="truncate">{title}</span>{count > 0 && <span className="pp-band-cnt">{count}</span>}</h3>
        {link && <button type="button" onClick={onLink} className="pp-band-link shrink-0">{link}</button>}
      </div>
      {meta && <p className="text-[13px] text-white/75 mt-0.5 truncate">{meta}</p>}
    </>
  );
}

function RequestsSlide({ items, bal, nav, onNew, adv, wide }) {
  // 258: jen žádosti, které čekají na vedoucího — schválené a zamítnuté přijdou do zvonečku
  const list = (items || []).filter((a) => a.status === "pending")
    .sort((x, y) => (x.date_from < y.date_from ? -1 : 1)).slice(0, 6);
  // zálohy vedle žádostí (246); bez zálohy mají žádosti celou šířku
  return (
    <div className={`grid gap-5 items-start ${adv && wide ? "grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]" : "grid-cols-1"}`}>
    <div className="pp-band-panel rounded-[18px] px-4 pt-3.5 pb-3 min-w-0" data-testid="band-requests">
      <PanelHead title="Moje žádosti" count={(items || []).filter((a) => a.status === "pending").length} link="Kalendář" onLink={() => nav("/dochazka")}
        meta={bal?.entitlement != null ? `Dovolená ${bal.year}: zbývá ${daysWord(bal.remaining)} z ${bal.entitlement}${bal.pending ? ` · čeká ${daysWord(bal.pending)}` : ""}` : "Volno, dovolená, lékař"} />
      {items === null ? <div className="h-24 mt-3 rounded-2xl bg-white/10 animate-pulse motion-reduce:animate-none" />
        : !list.length ? (
          <div className="py-3 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-[15px] font-bold">Žádná žádost nečeká na vedoucího</span>
            <button type="button" onClick={onNew} className="h-9 px-4 rounded-full bg-white text-[#a84d2b] text-sm font-bold">Požádat o volno</button>
          </div>
        ) : (
          <div className="mt-1">
            {list.map((a) => (
              <button key={a.id} type="button" onClick={() => nav("/dochazka")} data-testid={`req-row-${a.id}`}
                className="w-full grid grid-cols-[34px_minmax(0,1fr)_auto] gap-3 items-center py-2.5 border-b border-white/[.12] last:border-b-0 text-left hover:bg-white/[.04] rounded-[8px]">
                <span className="w-[34px] h-[34px] rounded-[10px] bg-white/10 grid place-items-center text-[12px] font-extrabold">{a.code}</span>
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-bold truncate">{a.type_label}{a.makeup ? " · ↺ napracuji" : ""} · <span className="tabular-nums">{a.when}</span></span>
                  <span className="block text-[12.5px] text-white/75 truncate">{a.status === "rejected" && a.reject_reason ? `Zamítnuto: ${a.reject_reason}` : a.note ? `„${a.note}“` : a.status === "pending" ? "čeká na vedoucího" : "schváleno"}</span>
                </span>
                <span className={`h-6 px-2.5 rounded-full text-[11px] font-extrabold uppercase grid place-items-center ${ABS_TONE[a.status] || "bg-white/15"}`}>{STATUS_LABEL[a.status]}</span>
              </button>
            ))}
          </div>
        )}
    </div>
    {adv && <MyAdvancesCard variant="band" />}
    </div>
  );
}

// ---------------- úvod ----------------
export default function DesktopHome() {
  const { user, company } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const role = user?.role;
  const { cardsOff } = useHomePrefs();   // 330: Můj Přehled — vypnuté karty pod osou
  const seeAtt = role === "OWNER" || role === "MANAGER";   // přepínač Já / Firma a celá firma v ose jen vedení
  const seeApprove = seeAtt || role === "ASSISTANT";      // schvalování docházky i asistentka (server jí to dovoluje) — 238
  const worker = !["OWNER", "MANAGER", "ASSISTANT"].includes(role);   // 246: dělník, skladník, subdodavatel
  const SH = worker ? 0 : SHIFT;

  // směna (MyShiftBlock headless posílá data, stejně jako mobilní přehled)
  const [mode, setMode] = useState(() => effectiveAttendanceMode(user, company));
  const [sh, setSh] = useState(null);
  const onShiftState = useCallback((st) => { setSh(st); setMode((m) => correctedMode(m, st?.ov)); }, []);
  const st = shiftStateFrom(sh?.ov, { mode: mode || "site", day: sh?.day, todayHours: sh?.todayHours });
  const working = st.state === "working" && !!st.since;
  const daysheet = st.mode === "daysheet";
  const myProjects = sh?.ov?.my_projects || [];
  const prefKey = `pp-site-${user?.id}`;
  const [pref, setPref] = useState(() => storage.get(prefKey) || null);
  const prefProj = myProjects.find((p) => p.id === pref) || null;
  const week = Number(sh?.pd?.hours?.week) || 0;

  // data pruhu a karet
  const [work, setWork] = useState(null);
  const [workErr, setWorkErr] = useState(false);
  const [absPend, setAbsPend] = useState(null);   // 250: žádosti o volno jsou v Co hoří
  const [hotBusy, setHotBusy] = useState(null);
  const loadWork = useCallback(() => {
    if (worker) return;
    api.get("/dashboard/work").then((r) => { setWork(r.data); setWorkErr(false); }).catch(() => setWorkErr(true));
    api.get("/absences/pending").then((r) => setAbsPend(r.data.items || [])).catch(() => setAbsPend([]));
  }, [worker]);
  useVisiblePolling(loadWork, 60000);
  const [scope, setScopeState] = useState(() => (storage.get("pp-home-scope") === "me" ? "me" : "firm"));
  const setScope = (v) => { setScopeState(v); try { storage.set("pp-home-scope", v); } catch { /* noop */ } };
  const meOnly = !seeAtt || scope === "me";   // asistentka vidí v ose jen svoje
  const [today, setToday] = useState(null);
  const loadToday = useCallback(() => {
    // 258: dělník má osu Dnes taky (server mu vrátí jen jeho směny, úkoly a doklady)
    api.get(`/dashboard/today${meOnly ? "?scope=me" : ""}`).then((r) => setToday(r.data)).catch(() => {});
  }, [meOnly]);
  // dělník: žádosti, dnešní směny, zítřejší úkoly, zálohy
  const [wAbs, setWAbs] = useState(null);
  const [wBal, setWBal] = useState(null);
  const [wRows, setWRows] = useState(null);
  const [wTasks, setWTasks] = useState(null);
  const [hasAdv, setHasAdv] = useState(false);
  const loadWorker = useCallback(() => {
    if (!worker) return;
    api.get("/absences").then((r) => setWAbs(r.data.items || [])).catch(() => setWAbs([]));
    api.get("/absences/balance").then((r) => setWBal(r.data)).catch(() => setWBal(null));
    // 254: i minulý měsíc — neodhlášená směna z konce měsíce musí jít doplnit i prvního
    const m0 = isoToday().slice(0, 7), d = new Date(`${m0}-01T12:00:00`); d.setMonth(d.getMonth() - 1);
    const m1 = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    Promise.all([m1, m0].map((m) => api.get(`/attendance/monthly?month=${m}`).then((r) => r.data.rows || []).catch(() => [])))
      .then(([a, b]) => setWRows([...a, ...b]));
    api.get("/tasks?status=open").then((r) => setWTasks(r.data || [])).catch(() => setWTasks([]));
    if (role !== "SUBCONTRACTOR") api.get("/cashbox/my-cases").then((r) => setHasAdv((r.data.cases || []).length > 0)).catch(() => setHasAdv(false));
  }, [worker, role]);
  useVisiblePolling(loadWorker, 120000);
  const [absOpen, setAbsOpen] = useState(false);
  useEffect(() => { setToday(null); }, [meOnly]);
  useVisiblePolling(loadToday, 60000);
  // schválení žádosti rovnou z řádku Co hoří (zamítnutí s důvodem v šichtovnici)
  const approveAbs = async (x) => {
    setHotBusy(x.key);
    try { await api.post(`/absences/${x.abs.id}/approve`); setAbsPend((a) => (a || []).filter((y) => y.id !== x.abs.id)); toast.success(x.abs.ack ? "Vzato na vědomí." : "Schváleno."); loadToday(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setHotBusy(null); }
  };
  const [nowMin, setNowMin] = useState(() => pragueMin(new Date()) ?? 0);
  useEffect(() => { const id = setInterval(() => setNowMin(pragueMin(new Date()) ?? 0), 60000); return () => clearInterval(id); }, []);
  const [aqKey, setAqKey] = useState(0);
  useEffect(() => {
    const changed = () => { loadWork(); loadToday(); loadWorker(); setAqKey((k) => k + 1); };
    window.addEventListener("pp-shift-changed", changed);
    window.addEventListener("pp-doc-saved", loadToday);
    return () => { window.removeEventListener("pp-shift-changed", changed); window.removeEventListener("pp-doc-saved", loadToday); };
  }, [loadWork, loadToday, loadWorker]);

  // pruh
  const [slide, setSlide] = useState(0);
  const bandPill = useSlidingPill(slide);   // 385: bílý podklad snímku dojede s pružinou
  const [attCount, setAttCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);   // 263: úkoly ke schválení (jen vedení)
  const [bandRef, bandW] = useWidth();
  const wide = bandW >= 940;
  const narrowTabs = bandW > 0 && bandW < 700;
  const slides = useRef([]);
  const [trackH, setTrackH] = useState(null);
  useLayoutEffect(() => {
    const el = slides.current[slide];
    if (!el) return undefined;
    const set = () => setTrackH(el.offsetHeight);
    set();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [slide]);
  const drag = useRef(null);
  const trackRef = useRef(null);
  const onDown = (e) => {
    if (e.button !== 0 || e.target.closest("button,a,input,select,textarea,[role=listbox]")) return;
    drag.current = { x: e.clientX, y: e.clientY, dx: 0, moved: false };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d || !trackRef.current) return;
    d.dx = e.clientX - d.x;
    if (Math.abs(d.dx) > 8 && Math.abs(d.dx) > Math.abs(e.clientY - d.y)) {
      d.moved = true;
      trackRef.current.style.transition = "none";
      trackRef.current.style.transform = `translateX(calc(-${slide * 100}% + ${d.dx}px))`;
    }
  };
  // 237: posun vrátit vždy na snímek, kde pruh skončí — prázdný transform by po kliknutí
  // do karty ukázal první snímek, zatímco záložka a barva zůstaly na vybraném
  const onUp = () => {
    const d = drag.current; drag.current = null;
    if (!d || !trackRef.current) return;
    const next = d.dx < -60 && slide < 2 ? slide + 1 : d.dx > 60 && slide > 0 ? slide - 1 : slide;
    if (d.moved) {
      trackRef.current.style.transition = "";
      trackRef.current.style.transform = `translateX(-${next * 100}%)`;
    }
    if (next !== slide) setSlide(next);
  };
  // 237–239: touchpad (Mac) — vodorovný pohyb dvěma prsty přepne snímek; jedno gesto = vždy jen jeden snímek.
  // Po švihu Mac ještě ~1 s posílá slábnoucí setrvačnost; během švihu pohyb kolísá. Nové gesto se proto
  // pozná, až setrvačnost skoro dojede (pohyb klesne pod 6 px) a pak znovu výrazně zesílí, nebo když
  // gesto změní směr — nejdřív 350 ms po předchozím přepnutí. Svislé posouvání stránky zůstává.
  useEffect(() => {
    const el = bandRef.current;
    if (!el) return undefined;
    let acc = 0, lock = false, lockAt = 0, low = Infinity, dirLock = 0, quiet = null;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) * 1.2) return;
      e.preventDefault();
      const dx = e.deltaMode === 1 ? e.deltaX * 16 : e.deltaX;
      const abs = Math.abs(dx), now = e.timeStamp || performance.now();
      clearTimeout(quiet);
      quiet = setTimeout(() => { acc = 0; lock = false; low = Infinity; }, 200);
      if (lock) {
        low = Math.min(low, abs);
        const later = now - lockAt > 350;
        const fresh = later && ((low <= 6 && abs >= 12 && abs > low * 3) || (abs >= 8 && Math.sign(dx) !== dirLock));
        if (!fresh) return;
        lock = false; acc = 0; low = Infinity;
      }
      acc += dx;
      if (Math.abs(acc) < 40) return;
      lock = true; lockAt = now; low = abs; dirLock = Math.sign(acc);
      const dir = acc > 0 ? 1 : -1;
      setSlide((sl) => Math.max(0, Math.min(2, sl + dir)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => { el.removeEventListener("wheel", onWheel); clearTimeout(quiet); };
  }, [bandRef]);

  // 258: nejstarší neodhlášená směna bez doplněného konce — Příchod je do doplnění zavřený
  // 260: jen když opravdu blokuje (ne stará směna ani člen s výjimkou) — jinak zůstane připomínka
  const fixFirst = worker && sh && !working && !daysheet && !["draft", "ended"].includes(st.state)
    ? (wRows || []).find((r) => needsEndFix(r) && r.blocks_check_in !== false) || null : null;
  const fixDay = fixFirst ? new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric", timeZone: TZ }).format(new Date(fixFirst.check_in)) : "";
  // tlačítko příchodu: klik = karta směny (+ seznam staveb, výběrem příchod), podržení = rovnou
  const onTap = () => {
    setSlide(SH);
    if (fixFirst) { setFixRow(fixFirst); return; }
    if (!sh) return;
    if (st.state === "draft" || (!working && !daysheet)) nav("/?shift=1");
  };
  const onHold = () => {
    setSlide(SH);
    if (fixFirst) { setFixRow(fixFirst); return; }
    if (!working && !daysheet && st.state !== "draft" && prefProj) nav(`/?shift=1&site=${encodeURIComponent(prefProj.id)}`);
    else nav("/?shift=1");
  };
  const shiftLabel = fixFirst ? "Doplň konec" : working ? (daysheet ? "Podrž · konec dne" : "Podrž · odchod") : st.state === "draft" ? "Vyplň výkaz"
    : st.state === "ended" ? `Hotovo · ${hoursOrMin(st.hours)}` : daysheet ? "Podrž · příchod" : "Příchod";
  const shiftSub = fixFirst ? `nebyl jsi odhlášen ${fixDay}` : working ? `${sh?.ov?.shift?.project_name || "Denní výkaz"} · od ${new Intl.DateTimeFormat("cs-CZ", { hour: "numeric", minute: "2-digit", timeZone: TZ }).format(new Date(st.since))}`
    : st.state === "draft" ? "rozděl hodiny na zakázky" : st.state === "ended" ? (daysheet ? "dnes odesláno" : "klikni pro další příchod")
    : daysheet ? "denní výkaz" : prefProj ? prefProj.name : "klikni a vyber stavbu";

  // výběr stavby na kartě (jen předvolba — příchod až tlačítkem)
  const [pickOpen, setPickOpen] = useState(false);
  const pickSite = (id) => { setPref(id); try { storage.set(prefKey, id); } catch { /* noop */ } setPickOpen(false); };

  // faktury subdodavatelů: okno schválení (i z odkazu ?subinvoice= v upozornění)
  const [subId, setSubId] = useState(() => params.get("subinvoice") || null);
  const subConsumed = () => {
    setSubId(null);
    if (params.get("subinvoice")) { const n = new URLSearchParams(params); n.delete("subinvoice"); setParams(n, { replace: true }); }
  };
  const [newTask, setNewTask] = useState(null);
  // 254: doplnění konce neodhlášené směny (i z odkazu ?konec= v upozornění)
  const [fixRow, setFixRow] = useState(null);
  const fixParam = params.get("konec");
  useEffect(() => {
    if (!fixParam || !wRows) return;
    const r = wRows.find((x) => x.id === fixParam);
    if (r && needsEndFix(r)) setFixRow(r);
    const n = new URLSearchParams(params); n.delete("konec"); setParams(n, { replace: true });
  }, [fixParam, wRows]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 237: barva pruhu podle směny — ráno před příchodem terakotový celý pruh; v práci zelená
  // jen Moje směna (ostatní snímky tmavé); po odchodu tmavý celý pruh
  const tone = !sh ? "" : working ? (slide === SH ? "green" : "") : fixFirst ? "red" : st.state === "ended" ? "" : "terra";
  const hot = work && absPend ? hotItems(work, absPend) : null;
  const nFin = hot ? hot.length : 0;

  // dělník: dnešní směny, úkoly na dnes, zítřek, upozornění
  const tIso = isoToday();
  const pragueDay = (iso) => new Intl.DateTimeFormat("sv-SE", { timeZone: TZ }).format(new Date(iso));
  const tmr = addDaysD(day0(new Date()), 1);
  const wTomorrow = wTasks === null ? null : wTasks.filter((t) => (t.assignees || []).includes(user?.id)).map(taskItem)
    .filter((t) => t && spansDay(t, tmr)).sort((a, b) => (a.inGrid === b.inGrid ? a._s - b._s : a.inGrid ? -1 : 1));
  // 258: Zítra v kartě Dnes — úkoly zítřka ve tvaru osy (/dashboard/today)
  const tomorrowFeed = worker ? {
    label: `${DOW_LONG[(tmr.getDay() + 6) % 7]} ${tmr.getDate()}. ${MONTHS_GEN[tmr.getMonth()]}`,
    empty: "Na zítřek nemáš naplánovaný žádný úkol — zeptej se stavbyvedoucího.",
    data: wTomorrow === null ? null : { today: pragueDay(tmr.toISOString()), people: [], docs: [], diary: [], diaryMissing: [], due: [],
      tasks: { today: wTomorrow.map((t) => ({ id: t.id, title: t.title, project_name: t.project_name, time_from: t.inGrid ? hmOf(t._s) : null, assignees: [String(user?.name || "").split(" ")[0]] })), overdue: [], done: [] } },
  } : null;
  const alerts = [];
  if (worker && sh?.day && sh.day.date < tIso && ["open", "draft", "returned"].includes(sh.day.status)) {
    // 260: člen s výjimkou (Tým › Nastavení) se přihlásí i bez výkazu — jen připomínka
    const lets = user?.attendance_never_block || company?.unfinished_days_block === 0;
    alerts.push({ key: "ds", block: !lets, title: `Neodeslaný výkaz z ${czDay(sh.day.date)}`, sub: lets ? "odešli ho, až budeš mít chvíli" : "bez něj se nepřihlásíš do práce", btn: "Vyplnit", to: "/?shift=1" });
  }
  // 254: neodhlášená směna — tlačítko otevře okno (přesčas / zapomněl + skutečný konec), ne Docházku
  (worker && !fixFirst ? wRows || [] : []).filter(needsEndFix).slice(-2)
    .forEach((r) => alerts.push({ key: r.id, title: `Nebyl jsi odhlášen ${czDay(pragueDay(r.check_in))}`, sub: `${r.project} · doplň, kdy jsi skončil`, btn: "Doplnit", fix: r }));
  const pendingReq = (wAbs || []).filter((a) => a.status === "pending").length;
  const qa = (Icon, label, fn, testid) => (
    <button key={testid} type="button" onClick={fn} data-testid={testid} className="pp-band-qa h-10 px-4 rounded-full border border-white/15 bg-white/[.03] text-sm font-bold inline-flex items-center gap-2 hover:bg-white/10">
      <Icon size={16} aria-hidden="true" />{label}
    </button>
  );

  const hr = Math.floor((pragueMin(new Date()) ?? 600) / 60);
  const greeting = hr < 10 ? "Dobré ráno" : hr < 18 ? "Dobrý den" : "Dobrý večer";
  const first = String(user?.name || "").trim().split(/\s+/)[0] || "";
  const dateStr = new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "long", timeZone: TZ }).format(new Date());
  const tab = (i, label, extra) => (
    <button type="button" role="tab" aria-selected={slide === i} onClick={() => setSlide(i)} data-testid={`band-tab-${i}`} data-pill-on={slide === i || undefined}
      className={`relative z-[1] h-[34px] px-3.5 rounded-full text-[13.5px] font-bold whitespace-nowrap inline-flex items-center gap-1.5 border transition-colors ${slide === i
        ? `${bandPill.ready ? "border-transparent" : "bg-white border-white"} ${tone === "green" ? "text-[#14684a]" : tone === "red" ? "text-[#a8281c]" : tone ? "text-[#a84d2b]" : "text-[#0f0f10]"}`
        : (tone ? "bg-black/[.14] border-white/25 text-white/90" : "bg-white/[.04] border-white/10 text-white/70 hover:text-white")}`}>
      {label}{extra}
    </button>
  );
  const cnt = (n, on) => n > 0 ? <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11.5px] font-extrabold grid place-items-center tabular-nums text-white ${tone ? (on ? "bg-[#7a3418]" : "bg-black/30") : "bg-brand"}`}>{n}</span> : null;

  return (
    <div className="space-y-4 lg:space-y-5" data-testid="desktop-home">
      <MyShiftBlock mode={mode} headless onState={onShiftState} />

      <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="home-header">
        <div className="min-w-0">
          <h1 className="font-heading text-[28px] lg:text-[32px] font-extrabold tracking-tight truncate">{greeting}{first ? `, ${vocative(first)}` : ""}</h1>
          <p className="text-sm text-muted-foreground truncate">{dateStr}{user?.company_name ? ` · ${user.company_name}` : ""}</p>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <HomeSearch className="hidden lg:block w-[240px] xl:w-[260px]" peopleSearch={!worker} />
          <HeaderShiftButton state={working ? "working" : st.state} hm={working ? elapsedHm(st.since) : ""} label={shiftLabel} sub={shiftSub} onTap={onTap} onHold={onHold} />
        </div>
      </div>

      {/* 306: průvodce nastavením není dokončený — karta jen na počítači a tabletu (telefon má tečku u avataru) */}
      <GuideCard />

      <section ref={bandRef} data-site-anchor aria-roledescription="carousel" aria-label={worker ? "Moje směna, hodiny a žádosti" : "Přehled firmy, moje směna a co hoří"}
        className={`pp-band ${tone} relative rounded-[26px] overflow-hidden transition-[background-color] duration-300`} data-testid="home-band"
        // 355: ne z otevřeného panelu / okna (portál probublá Reactem sem, i když v pruhu není)
        onKeyDown={(e) => { if (!e.currentTarget.contains(e.target) || e.target.closest("input,textarea")) return; if (e.key === "ArrowRight" && slide < 2) setSlide(slide + 1); if (e.key === "ArrowLeft" && slide > 0) setSlide(slide - 1); }}>
        <div ref={bandPill.ref} role="tablist" aria-label="Snímky" className="absolute top-4 left-5 right-[104px] z-[3] flex gap-1.5 overflow-x-auto no-scrollbar">
          {bandPill.pill("bg-white")}
          {worker ? (<>
            {tab(0, "Příchod", working && <><span className="w-2 h-2 rounded-full bg-[#3fbf8f] shadow-[0_0_0_3px_rgba(63,191,143,.25)]" /><span className="tabular-nums">{elapsedHm(st.since)} h</span></>)}
            {tab(1, narrowTabs ? "Hodiny" : "Moje hodiny")}
            {tab(2, "Žádosti", cnt(pendingReq, slide === 2))}
          </>) : (<>
          {tab(0, narrowTabs ? "Firma" : "Firma dnes")}
          {tab(SHIFT, narrowTabs ? "Směna" : seeApprove ? "Směna a ke schválení" : "Moje směna", <>
            {working && <><span className="w-2 h-2 rounded-full bg-[#3fbf8f] shadow-[0_0_0_3px_rgba(63,191,143,.25)]" /><span className="tabular-nums">{elapsedHm(st.since)} h</span></>}
            {seeApprove && cnt(attCount + (seeAtt ? taskCount : 0), slide === SHIFT)}
          </>)}
          {tab(FIN, "Co hoří", cnt(nFin, slide === FIN))}
          </>)}
        </div>
        <div className="absolute top-4 right-4 z-[3] flex gap-1.5">
          <button type="button" onClick={() => setSlide(Math.max(0, slide - 1))} disabled={slide === 0} aria-label="Předchozí"
            className="w-[34px] h-[34px] rounded-full bg-black/25 grid place-items-center disabled:opacity-35"><ChevronLeft size={16} /></button>
          <button type="button" onClick={() => setSlide(Math.min(2, slide + 1))} disabled={slide === 2} aria-label="Další"
            className="w-[34px] h-[34px] rounded-full bg-black/25 grid place-items-center disabled:opacity-35"><ChevronRight size={16} /></button>
        </div>
        <div ref={trackRef} className="pp-band-track flex items-start" style={{ transform: `translateX(-${slide * 100}%)`, height: trackH ?? undefined }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp}>
          {(worker ? [
            <ShiftSlide key="s" st={st} sh={sh} prefProj={prefProj} week={week} wide={wide} seeAtt={false} role={role} nav={nav} terra={!!tone} fix={fixFirst} onFix={setFixRow}
              onPick={() => setPickOpen(true)} extra={<WorkerAlerts alerts={alerts} nav={nav} onFix={setFixRow} />} />,
            <HoursChart key="h" variant="band" />,
            <RequestsSlide key="r" items={wAbs} bal={wBal} nav={nav} onNew={() => setAbsOpen(true)} adv={hasAdv} wide={wide} />,
          ] : [
            <FirmSlide key="f" work={work} week={week} role={role} wide={wide} narrow={narrowTabs} terra={tone === "terra"} nav={nav} onNewTask={() => setNewTask({})} />,
            <ShiftSlide key="s" st={st} sh={sh} prefProj={prefProj} week={week} wide={wide} seeAtt={seeApprove} role={role} nav={nav}
              seeTasks={seeAtt} attCount={attCount} taskCount={taskCount} onTaskCount={setTaskCount} active={slide === SHIFT}
              onPick={() => setPickOpen(true)} onAttCount={setAttCount} reloadKey={aqKey} onApproved={loadToday} />,
            <HotSlide key="i" items={hot} wide={wide} nav={nav} onSub={setSubId} onAbs={approveAbs} busy={hotBusy} active={slide === 2} onDocChanged={loadWork} />,
          ]).map((node, i) => (
            <div key={i} ref={(el) => { slides.current[i] = el; }} aria-hidden={slide !== i} inert={slide !== i ? "" : undefined}
              role="tabpanel" className="w-full shrink-0 min-w-0 px-5 lg:px-6 pt-[68px] pb-6" data-testid={`band-slide-${i}`}>
              {node}
            </div>
          ))}
        </div>
        {worker && slide === 0 && (   // 258: Úkol, Volno a Účtenka jen u snímku Příchod
          <div className="relative z-[2] px-5 lg:px-6 pb-5 -mt-2 flex flex-wrap gap-2" data-testid="worker-quick">
            {qa(Plus, narrowTabs ? "Úkol" : "Nový úkol", () => setNewTask({}), "wqa-task")}
            {qa(CalendarX2, narrowTabs ? "Volno" : "Žádost o volno", () => setAbsOpen(true), "wqa-absence")}
            {role === "SUBCONTRACTOR" ? qa(ReceiptText, narrowTabs ? "Faktura" : "Nahrát fakturu", () => nav("/moje-faktury?nahrat=1"), "wqa-invoice")
              // účtenka jen k záloze: ukázat zálohy v Žádostech a otevřít výběr souboru
              : hasAdv && qa(ReceiptText, narrowTabs ? "Účtenka" : "Nahrát účtenku", () => { setSlide(2); setTimeout(() => window.dispatchEvent(new Event("pp-advance-receipt")), 80); }, "wqa-receipt")}
          </div>
        )}
        {workErr && !work && (
          <div className="absolute bottom-3 right-4 z-[3]">
            <button type="button" onClick={loadWork} className="h-9 px-3.5 rounded-full bg-white/15 text-sm font-bold inline-flex items-center gap-1.5"><RefreshCw size={14} />Načíst znovu</button>
          </div>
        )}
      </section>

      {/* ostatní schvalování — samy se schovají, když nic nečeká */}
      <div className="space-y-4 empty:hidden">
        {!worker && <MaterialApproval />}
        {!worker && <MyAdvancesCard />}
      </div>

      {worker ? (
        <TodayCard data={today} nowMin={nowMin} meId={user?.id} tomorrow={tomorrowFeed}
          onOpen={(e, setFilter) => {
            const a = e.act || {};
            if (a.type === "filter") return setFilter(a.f);
            if (a.type === "task") return nav(`/ukoly/${a.id}`);
            if (a.type === "tasks") return nav("/ukoly");
            if (a.type === "order") return nav(`/objednavky?o=${a.id}`);
            if (a.type === "person") {
              const r = (wRows || []).find((x) => x.id === a.row.id);
              return needsEndFix(r) ? setFixRow(r) : nav("/dochazka");   // neodhlášená směna → rovnou okno
            }
            return undefined;
          }} />
      ) : (<>
      <TodayCard data={today} scope={meOnly ? "me" : "firm"} onScope={setScope} showScope={seeAtt} managerScope={role === "MANAGER"} prefsCards
        nowMin={nowMin} meId={user?.id}
        onOpen={(e, setFilter) => {
          const a = e.act || {};
          if (a.type === "filter") return setFilter(a.f);
          if (a.type === "task") return nav(`/ukoly/${a.id}`);
          if (a.type === "tasks") return nav("/ukoly");
          if (a.type === "doc") return nav("/ekonomika/doklady");
          if (a.type === "order") return nav(`/objednavky?o=${a.id}`);
          // 263: odkud přišel (Zpět v zakázce se vrátí sem i v náhledu Emergentu, kde historie prohlížeče nefunguje)
          if (a.type === "diary") return nav(a.projectId ? `/zakazky/${a.projectId}?tab=diary` : "/denik", { state: { from: "/" } });
          if (a.type === "person") {
            if (seeApprove && !a.row.running && a.row.approval === "pending") { setSlide(SHIFT); window.scrollTo({ top: 0, behavior: "smooth" }); return undefined; }
            return nav("/dochazka");
          }
          return undefined;
        }} />

      {(!cardsOff.includes("zitra") || !cardsOff.includes("terminy")) && (
      <div className={`grid gap-4 items-start ${bandW >= 900 && cardsOff.length === 0 ? "grid-cols-2" : "grid-cols-1"}`}>
        {!cardsOff.includes("zitra") && <TomorrowCard plan={work?.tomorrowPlan}
          onOpenTasks={() => nav(`/ukoly?pohled=lide&den=${work?.tomorrowPlan?.date || ""}`)}
          onOpenProject={(g) => nav(`/ukoly?pohled=lide&den=${work?.tomorrowPlan?.date || ""}${g.projectId ? `&project=${g.projectId}` : ""}`)}
          onNewTask={(ids) => setNewTask({ date: parseLocal(work?.tomorrowPlan?.date), assignees: ids })} />}
        {!cardsOff.includes("terminy") && <DeadlinesCard items={work ? work.upcomingDeadlines || [] : null} onOpen={() => nav("/planovani")} />}
      </div>)}
      </>)}

      <SitePicker open={pickOpen} onOpenChange={setPickOpen} title="Kde dnes pracuješ?" subtitle="Příchod pak zapíšeš podržením tlačítka vpravo nahoře."
        projects={myProjects} selectedId={pref} onPick={pickSite} />
      {!worker && <SubInvoiceApproval headless openId={subId} onOpened={subConsumed} onChanged={loadWork} />}
      {worker && <ShiftEndFixDialog row={fixRow} open={!!fixRow} onOpenChange={(o) => { if (!o) setFixRow(null); }} onSaved={loadWorker} />}
      {worker && <AbsenceDialog open={absOpen} onClose={() => setAbsOpen(false)} mode="request"
        initial={{ date_from: pragueDay(tmr.toISOString()), date_to: pragueDay(tmr.toISOString()) }} onSaved={loadWorker} />}
      <NewTaskDialog hideTrigger open={!!newTask} onOpenChange={(o) => { if (!o) setNewTask(null); }} initial={newTask?.date ? newTask : undefined}
        onCreated={() => { setNewTask(null); loadWork(); loadToday(); }} />
    </div>
  );
}
